import { unit } from 'mathjs';
import { fillingStatusFromValue } from 'components/utils/fillingType';
import { getFullCapacity, usesFullCapacity } from '../../fullVolume';
import { getUnit, resolveUnit } from '../../columnMappingAccessors';
import { hasValue } from '../rowContext';

const matchesExpectedVolume = (raw, mappings, expectedVolume) => {
  if (expectedVolume === null || !hasValue(raw) || !['number', 'string'].includes(typeof raw)) return false;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return false;
  try {
    const measuredLiters = unit(value, getUnit(mappings, 'liquidVolume', 'L')).toNumber('L');
    const resultUnit = resolveUnit(mappings, ['volumeAt20cResult', 'liquidVolumeResult'], 'L');
    const expectedLiters = unit(expectedVolume, resultUnit).toNumber('L');
    if (!Number.isFinite(measuredLiters) || !Number.isFinite(expectedLiters)) return false;
    // Only absorb floating-point conversion noise, not meaningful differences or display rounding.
    const tolerance = 1e-9 * Math.max(1, Math.abs(measuredLiters), Math.abs(expectedLiters));
    return Math.abs(measuredLiters - expectedLiters) <= tolerance;
  } catch (_) {
    return false;
  }
};

// Full rows are calculated from their capacity and empty rows are zero: measurements
// kept on them are ignored. A full row needs a valid capacity to be calculated.
// A direct volume equal to the volume actually used is not reported.
export default function fillingMeasurementIssues(row, mappings) {
  const filling = fillingStatusFromValue(row[mappings.filling?.csvName]);
  const full = usesFullCapacity(row, mappings);
  if (!full && filling !== 'empty') return [];
  const capacity = full ? getFullCapacity(row, mappings) : null;
  const invalidCapacity = capacity?.value === null;
  const expectedVolume = full ? capacity.value : 0;
  const measurements = ['liquidVolume', 'liquidHeight', 'emptyHeight'].flatMap((field) => {
    const mapping = mappings[field];
    if (!mapping?.csvName || !hasValue(row[mapping.csvName])) return [];
    if (field === 'liquidVolume' && matchesExpectedVolume(row[mapping.csvName], mappings, expectedVolume)) return [];
    return [{ field: mapping.csvName, value: row[mapping.csvName], unit: mapping.unit || '' }];
  });
  const values = measurements.filter((measurement, position) => (
    measurements.findIndex((entry) => entry.field === measurement.field) === position
  ));
  const issues = [];
  if (invalidCapacity) {
    const usable = capacity.field === mappings.usableCapacity?.csvName;
    issues.push({
      code: usable ? 'capacity_invalid_usable' : 'capacity_invalid',
      severity: 'error',
      params: { field: capacity.field },
      values: [],
    });
  }
  if (values.length) {
    issues.push({
      code: `measurement_ignored_${filling}`,
      severity: 'warning',
      params: {},
      values,
    });
  }
  return issues;
}
